/**
 * Централизованные утилиты для обработки ошибок
 * Устраняют дублирование кода обработки ошибок по всему приложению
 */

import { parseError } from './parseError';

/**
 * Опции для обработчиков ошибок
 */
interface ErrorHandlerOptions {
  /** Показывать ли console.error (по умолчанию true) */
  logToConsole?: boolean;
  /** Префикс для console.error сообщения */
  consolePrefix?: string;
  /** Пробрасывать ли ошибку дальше (по умолчанию false) */
  rethrow?: boolean;
}

/**
 * Универсальная функция обработки ошибок API
 * 
 * @example
 * ```typescript
 * try {
 *   await someApiCall();
 * } catch (error) {
 *   handleApiError(error, {
 *     consolePrefix: 'Failed to load data'
 *   });
 * }
 * ```
 */
export function handleApiError(
  error: unknown,
  options: ErrorHandlerOptions = {}
): void {
  const {
    logToConsole = true,
    consolePrefix = 'API Error',
    rethrow = false,
  } = options;

  // Парсим ошибку
  const appError = parseError(error);

  // Логируем в консоль если нужно
  if (logToConsole) {
    console.error(`${consolePrefix}:`, appError.originalError || appError);
  }

  // Пробрасываем ошибку если нужно
  if (rethrow) {
    throw appError;
  }
}

/**
 * Создает стандартизированный onError callback для React Query мутаций
 * 
 * @example
 * ```typescript
 * useMutation({
 *   mutationFn: deleteItem,
 *   onError: createMutationErrorHandler({ consolePrefix: 'Delete item error' }),
 * })
 * ```
 */
export function createMutationErrorHandler(
  options: Omit<ErrorHandlerOptions, 'rethrow'> = {}
): (error: Error) => void {
  return (error: Error) => {
    handleApiError(error, {
      ...options,
      rethrow: false,
    });
  };
}

/**
 * Обрабатывает ошибки в try-catch блоках с выбросом AppError
 * Используется в queryFn React Query
 */
export function handleQueryError(
  error: unknown,
  options: Omit<ErrorHandlerOptions, 'rethrow'> = {}
): never {
  handleApiError(error, {
    ...options,
    rethrow: true,
  });
  // TypeScript guard — функция всегда выбрасывает ошибку
  throw parseError(error);
}
